<!-- eslint-disable vue/multi-word-component-names -->
<script setup>
import { ref, computed, h, useAttrs } from 'vue'
import { useFormDisabled, useFormSize } from 'element-plus'
import DeptTag from './DeptTag.vue'
import DeptPicker from './DeptPicker.vue'

defineOptions({
  inheritAttrs: false
})

const DeptAddIcon = {
  props: { size: { type: Number, default: 16 } },
  render() {
    return h('svg', {
      fill: 'none',
      stroke: 'currentColor',
      viewBox: '0 0 24 24',
      style: { width: `${this.size}px`, height: `${this.size}px` }
    }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4'
      })
    ])
  }
}

const $props = defineProps({
  modelValue: {
    type: [Array, String, Number],
    default: undefined
  },
  multiple: {
    type: Boolean,
    default: true
  },
  disabled: {
    type: Boolean,
    default: false
  },
  placeholder: {
    type: String,
    default: '请选择部门'
  },
  hideAddButton: {
    type: Boolean,
    default: false
  }
})

const $emits = defineEmits(['update:modelValue'])
const attrs = useAttrs()

const pickerRef = ref()
const formDisabled = useFormDisabled()
const formSize = useFormSize()
const disabled = computed(() => {
  return formDisabled.value || $props.disabled
})
const sizeName = computed(() => {
  const s = formSize.value || 'default'
  if (s === 'small') return 'small'
  if (s === 'large') return 'large'
  return 'default'
})
const isEmpty = computed(() => {
  if (!$props.modelValue) return true
  return Array.isArray($props.modelValue) && $props.modelValue.length === 0
})

const valueArr = computed(() => {
  if (!$props.modelValue) return []
  return Array.isArray($props.modelValue) ? $props.modelValue : [$props.modelValue]
})

const openPicker = () => {
  pickerRef.value?.open()
}

const onClose = (id) => {
  if (!$props.modelValue) return
  if ($props.multiple && Array.isArray($props.modelValue)) {
    const newValue = $props.modelValue.filter(item => {
      const itemId = typeof item === 'object' ? item.id : item
      return String(itemId) !== String(id)
    })
    $emits('update:modelValue', newValue)
  } else {
    $emits('update:modelValue', null)
  }
}
</script>

<template>
  <DeptPicker ref="pickerRef" :multiple="multiple" :model-value="modelValue" @update:modelValue="$emit('update:modelValue', $event)" />
  <div
    v-bind="attrs"
    class="dept-selector dept-wrapper"
    :class="{ 'is-disabled': disabled, 'is-empty': isEmpty }"
  >
    <DeptTag
      v-for="item in valueArr"
      :closable="!disabled"
      :disabled="disabled"
      :key="typeof item === 'object' ? item.id : item"
      :id="typeof item === 'object' ? item.id : item"
      :name="typeof item === 'object' ? item.name : ''"
      @close="onClose(typeof item === 'object' ? item.id : item)"
    />
    <span v-show="isEmpty" class="dept-selector-placeholder">{{ placeholder }}</span>
    <button
      v-if="!hideAddButton && !disabled"
      type="button"
      class="dept-selector-add"
      :class="`is-${sizeName}`"
      @click="openPicker"
    >
      <DeptAddIcon :size="sizeName === 'small' ? 12 : 14" />
      <span class="dept-selector-add-text">{{ isEmpty ? '添加' : '' }}</span>
    </button>
  </div>
</template>

<style scoped lang="scss">
.dept-selector {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-width: 0;
  min-height: var(--form-control-height, 44px);
  max-height: 96px;
  padding: 5px 8px;
  overflow-x: hidden;
  overflow-y: auto;
  border: 1px solid var(--form-control-border, #cbd5e1);
  border-radius: var(--form-control-radius, 0.75rem);
  background-color: var(--form-control-bg, #ffffff);
  box-shadow: var(--form-control-shadow, 0 1px 2px rgba(15, 23, 42, 0.06));
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;

  &:hover {
    border-color: var(--form-control-border-hover, #94a3b8);
  }

  &:focus-within {
    border-color: var(--form-control-focus, #2563eb);
    box-shadow: var(--form-control-focus-ring, 0 0 0 3px rgba(37, 99, 235, 0.14));
  }

  &.is-disabled {
    background-color: var(--form-control-disabled-bg, #f8fafc);
    color: var(--form-control-disabled-text, #94a3b8);
    cursor: not-allowed;
  }

  &.form-control-sm,
  &.selector-compact {
    min-height: var(--form-control-height-sm, 36px);
    max-height: 84px;
    padding: 3px 6px;
    gap: 4px;
    border-radius: var(--form-control-radius-sm, 0.625rem);
  }
}

.dept-selector-placeholder {
  flex: 1 1 auto;
  min-width: 0;
  color: var(--form-control-placeholder, #94a3b8);
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dept-selector.form-control-sm .dept-selector-placeholder,
.dept-selector.selector-compact .dept-selector-placeholder {
  font-size: 13px;
  line-height: 18px;
}

.dept-selector.is-disabled .dept-selector-placeholder {
  color: var(--form-control-disabled-text, #94a3b8);
}

.dept-selector-add {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 26px;
  min-width: 26px;
  padding: 0 8px;
  margin-left: auto;
  border: 1px dashed var(--form-control-border, #cbd5e1);
  border-radius: 9999px;
  background-color: transparent;
  color: #64748b;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  transition: border-color 0.16s ease, color 0.16s ease, background-color 0.16s ease;

  &:hover {
    border-color: var(--form-control-focus, #2563eb);
    border-style: solid;
    color: var(--form-control-focus, #2563eb);
    background-color: #eff6ff;
  }

  &.is-small {
    height: 22px;
    min-width: 22px;
    padding: 0 6px;
    font-size: 11px;
  }

  &.is-large {
    height: 30px;
    min-width: 30px;
    padding: 0 10px;
    font-size: 13px;
  }
}

.dept-selector.is-disabled .dept-selector-add {
  display: none;
}

.dept-selector-add-text {
  font-weight: 500;
  white-space: nowrap;
}

.dept-selector:not(.is-empty) .dept-selector-add-text {
  display: none;
}

.dept-selector:not(.is-empty) .dept-selector-add {
  padding: 0;
  min-width: 26px;
}

.dept-selector.form-control-sm:not(.is-empty) .dept-selector-add,
.dept-selector.selector-compact:not(.is-empty) .dept-selector-add {
  min-width: 22px;
}

.dept-selector.form-control-sm :deep(.dept-tag),
.dept-selector.selector-compact :deep(.dept-tag) {
  --tag-h: 24px;
  --tag-pad-x: 3px;
  --tag-gap: 4px;
  --tag-icon: 18px;
  --tag-font: 12px;
}

.dept-selector.form-control-sm :deep(.dept-tag-close),
.dept-selector.selector-compact :deep(.dept-tag-close) {
  width: 14px;
  height: 14px;

  svg {
    width: 9px;
    height: 9px;
  }
}
</style>
